import api from '@/lib/api';
import type { PageResponse } from '@/types/admin.model';
import { toPageResponse } from '@/types/admin.model';
import type { TournamentStatus } from '@/types/tournament.model';

/**
 * Shape fixed by the backend: GET /tournaments/admin builds its own DTO by hand
 * instead of projecting TournamentDTO, so there is no `fields` param to send.
 */
export interface AdminTournament {
  id: string;
  name: string;
  status: TournamentStatus;
  startDate: string;
  endDate: string | null;
  sport: string;
  clubNames: string[];
  ownerPk: string;
  ownerName: string;
  ownerEmail: string;
}

/**
 * Sports the endpoint filters by. They are `SportTypeEnum` names on the backend
 * (`SportTypeEnum.kt`), which is also what `AdminTournament.sport` comes back
 * as: the DTO carries the sport's uid, and every sport row is keyed by the enum
 * name ('PADEL', 'TENNIS', ...).
 */
export const ADMIN_TOURNAMENT_SPORTS = [
  'PADEL',
  'TENNIS',
  'SQUASH',
  'FOOTBALL',
] as const;

export type AdminTournamentSport = (typeof ADMIN_TOURNAMENT_SPORTS)[number];

interface ListParams {
  query?: string;
  status?: TournamentStatus | '';
  sport?: AdminTournamentSport | '';
  page: number;
  size: number;
}

export const adminTournamentsApi = {
  list: async ({
    query,
    status,
    sport,
    page,
    size,
  }: ListParams): Promise<PageResponse<AdminTournament>> => {
    const response = await api.get('/tournaments/admin', {
      params: {
        query: query || undefined,
        status: status || undefined,
        sport: sport || undefined,
        page,
        size,
      },
    });
    return toPageResponse<AdminTournament>(response.data);
  },
};
